import { useEffect, useState } from 'react';

/**
 * IntroAnimation
 * ──────────────
 *  - Phase 1 (700ms): full-screen black overlay with the logo big in the centre.
 *  - Phase 2 (900ms): the logo shrinks and flies to roughly where the navbar
 *    logo sits in the top-left; at the same time the overlay fades out and
 *    the page underneath becomes visible.
 *  - Phase 3: unmounts.
 *
 * Plays once per browser-tab session (sessionStorage flag).
 */
type Phase = 'intro' | 'shrinking' | 'done';

const STORAGE_KEY = 'mp.introPlayed';

export default function IntroAnimation() {
  const [phase, setPhase] = useState<Phase>(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1' ? 'done' : 'intro';
    } catch {
      return 'intro';
    }
  });

  useEffect(() => {
    if (phase === 'done') return;
    const t1 = setTimeout(() => setPhase('shrinking'), 700);
    const t2 = setTimeout(() => {
      setPhase('done');
      try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
    }, 1700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (phase === 'done') return null;

  const shrunk = phase === 'shrinking';

  // Approximate the navbar logo's pixel position:
  //  - top: vertically centred in a 64px nav with a 36px logo → 14px
  //  - left: navbar uses max-w-6xl (1152px) centred + 24px padding, so on
  //    wide screens the logo sits further from the edge. The max() keeps
  //    it pinned at 24px on narrow screens and slides outwards on wide ones.
  const targetTop  = '14px';
  const targetLeft = 'max(24px, calc((100vw - 1152px) / 2 + 24px))';

  return (
    <div
      aria-hidden
      className="fixed inset-0 z-[100] pointer-events-none"
      style={{
        background: 'black',
        opacity:    shrunk ? 0 : 1,
        transition: 'opacity 700ms ease-out 300ms',
      }}
    >
      <img
        src="/logo.png"
        alt=""
        draggable={false}
        className="rounded-full object-contain select-none
                   shadow-[0_0_80px_rgba(234,179,8,0.45)]"
        style={{
          position:  'absolute',
          top:       shrunk ? targetTop  : '50%',
          left:      shrunk ? targetLeft : '50%',
          width:     shrunk ? '36px' : '208px',
          height:    shrunk ? '36px' : '208px',
          transform: shrunk ? 'translate(0, 0)' : 'translate(-50%, -50%)',
          transition: [
            'top 900ms cubic-bezier(0.7, 0, 0.3, 1)',
            'left 900ms cubic-bezier(0.7, 0, 0.3, 1)',
            'width 900ms cubic-bezier(0.7, 0, 0.3, 1)',
            'height 900ms cubic-bezier(0.7, 0, 0.3, 1)',
            'transform 900ms cubic-bezier(0.7, 0, 0.3, 1)',
          ].join(', '),
          willChange: 'top, left, width, height, transform',
        }}
      />
    </div>
  );
}
